import { useEffect, useState } from 'react';
import { useUser, useSession } from '@clerk/nextjs';
import { useSupabaseClient } from '@/lib/supabase';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  company_name: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  updated_at: string;
  user_id: string | null;
}

export function useUserProfile() {
  const { user, isLoaded } = useUser();
  const { session } = useSession();
  const supabase = useSupabaseClient();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    let mounted = true;

    async function fetchProfile() {
      if (!isLoaded) return;

      if (!user?.id) {
        if (mounted) {
          setProfile(null);
          setLoading(false);
        }
        return;
      }

      try {
        if (mounted) {
          setLoading(true);
          setError(null);
        }

        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .abortSignal(abortController.signal)
          .single();

        if (!mounted || abortController.signal.aborted) {
          return;
        }

        if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
          throw error;
        }

        if (mounted) {
          setProfile(data);
          setError(null);
        }
      } catch (err) {
        if (mounted && !abortController.signal.aborted) {
          console.error('Error fetching user profile:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch profile');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchProfile();

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, [user?.id, isLoaded]);

  return { profile, loading, error };
}